import { createAction, props } from '@ngrx/store';
import { CustomerInfo, PurchasesOfferings, PurchasesStoreProduct } from '@revenuecat/purchases-capacitor';

import { StripePurchaseEnum } from '../../enums/monetization';
import { ProfileSearchEnum } from '../../enums/profile-search';
import {
  MonetizationStripeProduct,
  RevenueCatWebAPICustomerInfo,
  RevenueCatWebAPIOfferings,
  StripeSubscriptionModel
} from '../../typings/monetization';
import { StoreProduct } from '../../typings/system-configurations';
import { WalkingFarmPhoneAndEmailPurchasePreviewResponse } from '../../typings/walking-farm';

export const initializeWeb = createAction(
  '[Monetization] Initialize Web',
  props<{
    payload: {
      id: string;
    };
  }>()
);

export const loadSubscriptions = createAction('[Monetization] Load Subscriptions');

export const loadSubscriptionsSuccess = createAction(
  '[Monetization] Load Subscriptions Success',
  props<{
    payload: StripeSubscriptionModel[];
  }>()
);
export const loadSubscriptionsFailure = createAction(
  '[Monetization] Load Subscriptions Failure',
  props<{
    payload: {
      error: Error;
    };
  }>()
);

export const cancelSubscription = createAction(
  '[Monetization] Cancel Subscription',
  props<{
    payload: {
      id: string;
    };
  }>()
);

export const cancelSubscriptionSuccess = createAction('[Monetization] Cancel Subscription Success');

export const cancelSubscriptionFailure = createAction(
  '[Monetization] Cancel Subscription Failure',
  props<{ payload: { error: Error } }>()
);

export const initializeMobile = createAction(
  '[Monetization] Initialize Mobile',
  props<{
    payload: {
      id: string;
    };
  }>()
);

export const initializeMobileSuccess = createAction('[Monetization] Initialize Mobile Success');

export const initializeMobileFailure = createAction(
  '[Monetization] Initialize Mobile Failure',
  props<{ error: Error }>()
);

export const loadPrimarySubscriptionDetails = createAction('[Monetization] Load Primary Subscription Details');

export const updateMobilePurchasesOfferings = createAction(
  '[Monetization] Update Mobile Purchases Offerings',
  props<{
    payload: {
      purchasesOfferings: PurchasesOfferings;
    };
  }>()
);

export const updateMobilePurchasesOfferingsFailure = createAction(
  '[Monetization] Update Mobile Purchase Offerings Failure',
  props<{ error: Error }>()
);

export const updateMobileCustomerInfo = createAction(
  '[Monetization] Update Mobile Customer Info',
  props<{
    payload: {
      customerInfo: CustomerInfo;
    };
  }>()
);

export const updateMobileCustomerInfoFailure = createAction(
  '[Monetization] Update Mobile Customer Info Failure',
  props<{ error: Error }>()
);

export const loadWebPurchasesOfferingsSuccess = createAction(
  '[Monetization] Load Web Purchases Offerings Success',
  props<{
    data: {
      purchasesOfferings: RevenueCatWebAPIOfferings;
    };
  }>()
);

export const loadWebPurchasesOfferingsFailure = createAction(
  '[Monetization] Load Web Purchases Offerings Failure',
  props<{ error: Error }>()
);

export const loadWebCustomerInfoSuccess = createAction(
  '[Monetization] Load Web Customer Info Success',
  props<{
    data: {
      customerInfo: RevenueCatWebAPICustomerInfo;
    };
  }>()
);

export const loadWebCustomerInfoFailure = createAction(
  '[Monetization] Load Web Customer Info Failure',
  props<{ error: Error }>()
);

export const loadPrimarySubscriptionDetailsSuccess = createAction(
  '[Monetization] Load Primary Subscription Details Success',
  props<{
    data: {
      description: string;
    };
  }>()
);
export const loadPrimarySubscriptionDetailsFailure = createAction(
  '[Monetization] Load Primary Subscription Details Failure',
  props<{ error: Error }>()
);

export const loadPrimarySubscriptionWebDetailsSuccess = createAction(
  '[Monetization] Load Primary Subscription Web Details Success',
  props<{
    data: MonetizationStripeProduct;
  }>()
);

export const loadPrimarySubscriptionWebDetailsFailure = createAction(
  '[Monetization] Load Primary Subscription Web Details Failure',
  props<{ error: Error }>()
);

export const paywallPurchase = createAction(
  '[Monetization] Paywall Purchase',
  props<{
    data: {
      product: PurchasesStoreProduct | null;
      purchaseType: StripePurchaseEnum;
      redirectBackOnSuccess: boolean;
      webPriceId: string | null;
    };
    searchType: ProfileSearchEnum;
  }>()
);

export const paywallWebPurchase = createAction(
  '[Monetization] Paywall Web Purchase',
  props<{
    payload: {
      redirectBackOnSuccess: boolean;
      searchType: ProfileSearchEnum;
      sessionId: string;
    };
  }>()
);

export const paywallWebPurchaseSuccess = createAction('[Monetization] Paywall Web Purchase Success');

export const paywallWebPurchaseFailure = createAction(
  '[Monetization] Paywall Web Purchase Failure',
  props<{ error: Error }>()
);

export const paywallMobilePurchaseSuccess = createAction(
  '[Monetization] Paywall Mobile Purchase Success',
  props<{
    payload: {
      customerInfo: CustomerInfo;
      redirectBackOnSuccess: boolean;
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const paywallMobilePurchaseFailure = createAction(
  '[Monetization] Paywall Mobile Purchase Failure',
  props<{
    payload: {
      error: Error;
    };
  }>()
);

export const persistAndRedirectToPurchase = createAction(
  '[Monetization] Update Web Redirect Back Url',
  props<{
    payload: {
      redirectBackOnSuccess: boolean;
      redirectBackUrl: string;
      searchType: ProfileSearchEnum;
      sessionId: string;
    };
  }>()
);

export const loadStoreWebDetails = createAction('[Monetization] Load Store Web Details');

export const loadStoreWebDetailsSuccess = createAction(
  '[Monetization] Load Store Web Details Success',
  props<{
    payload: MonetizationStripeProduct[];
  }>()
);

export const loadStoreWebDetailsFailure = createAction(
  '[Monetization] Load Store Web Details Failure',
  props<{ payload: { error: Error } }>()
);

export const updateSelectedStoreWebProductDetails = createAction(
  '[Monetization] Update Selected Store Web Product Details',
  props<{
    payload: StoreProduct;
  }>()
);

export const updateSelectedStoreWebProductDetailsSuccess = createAction(
  '[Monetization] Update Selected Store Web Product Details Success',
  props<{
    payload: {
      description: string;
    };
  }>()
);

export const updateSelectedStoreWebProductDetailsFailure = createAction(
  '[Monetization] Update Selected Store Web Product Details Failure',
  props<{ payload: { error: Error } }>()
);

export const logUserPurchase = createAction(
  '[Monetization] Log User Purchase',
  props<{ payload: { resumePurchaseFlow: boolean; searchType: ProfileSearchEnum } }>()
);

export const logUserPurchaseSuccess = createAction(
  '[Monetization] Log User Purchase Success',
  props<{ payload: { resumePurchaseFlow: boolean; searchType: ProfileSearchEnum } }>()
);

export const logUserPurchaseFailure = createAction(
  '[Monetization] Log User Purchase Failure',
  props<{ payload: { error: Error } }>()
);

export const openPurchasePhoneAndEmailModal = createAction(
  '[Monetization] Open Purchase Phone And Email Modal',
  props<{
    payload: {
      farmId: string;
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const purchasePhoneAndEmailPreview = createAction(
  '[Monetization] Purchase Phone And Email Preview',
  props<{
    payload: {
      farmId: string;
      propertyIds: string[];
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const resumePurchasePhoneAndEmailPreview = createAction(
  '[Monetization] Resume Purchase Phone And Email Preview',
  props<{
    payload: {
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const purchasePhoneAndEmailPreviewSuccess = createAction(
  '[Monetization] Purchase Phone And Email Preview Success',
  props<{
    payload: {
      farmId: string;
      preview: WalkingFarmPhoneAndEmailPurchasePreviewResponse;
      propertyIds: string[];
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const purchasePhoneAndEmailPreviewFailure = createAction(
  '[Monetization] Purchase Phone And Email Preview Failure',
  props<{ payload: { error: Error } }>()
);

export const purchasePhoneAndEmailSuccess = createAction('[Monetization] Purchase Phone And Email Success');

export const purchasePhoneAndEmailFailure = createAction(
  '[Monetization] Purchase Phone And Email Failure',
  props<{ payload: { error: Error } }>()
);

export const purchasePhoneAndEmailPurchaseMore = createAction(
  '[Monetization] Purchase Phone And Email Purchase More',
  props<{
    payload: {
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const purchasePhoneAndEmailConfirm = createAction(
  '[Monetization] Purchase Phone And Email Confirm',
  props<{
    payload: {
      farmId: string;
      propertyIds: string[];
    };
  }>()
);

export const updatePhoneAndEmailStripeCheckoutRedirectFarmId = createAction(
  '[Monetization] Update Phone And Email Stripe Checkout Redirect Farm Id',
  props<{
    payload: {
      phoneAndEmailStripeCheckoutRedirectFarmId: string | null;
    };
  }>()
);

export const restorePurchases = createAction('[Monetization] Restore Purchases');

export const restorePurchasesFailure = createAction(
  '[Monetization] Restore Purchases Failure',
  props<{ payload: { error: Error } }>()
);
